import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowDown } from "lucide-react";
import { useI18n } from "../../i18n";
import { CONTACT, HERO_IMG } from "../../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
  }),
};

export default function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const lines = [t.hero.l1, t.hero.l2, t.hero.l3];

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      {/* Background image, right side */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 lg:left-[52%]"
      >
        <img src={HERO_IMG} alt="Carpe Diem hair" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0a] via-[#0b0b0a]/70 to-transparent lg:via-[#0b0b0a]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0a] via-transparent to-[#0b0b0a]/40" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-body text-[11px] md:text-xs uppercase tracking-[0.42em] text-lime mb-6"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-display font-light text-[clamp(2.9rem,9vw,7.5rem)] leading-[0.95] tracking-[-0.02em] text-cream max-w-[15ch]">
          {lines.map((l, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 2 ? <em className="italic text-lime">{l}</em> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="font-body text-base md:text-lg text-cream/70 max-w-[46ch] mt-8 leading-relaxed"
        >
          {t.hero.lead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="group inline-flex items-center gap-2.5 bg-lime text-[#0b0b0a] font-body font-semibold text-sm px-7 py-4 rounded-full hover:bg-[#b6ff3c] transition-all hover:scale-[1.03]"
          >
            <MessageCircle size={18} /> {t.cta.book}
          </a>
          <a
            href={CONTACT.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center gap-2.5 border border-white/25 text-cream font-body text-sm px-7 py-4 rounded-full hover:border-lime hover:text-lime transition-all"
          >
            <Phone size={17} /> {CONTACT.phoneDisplay}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-body text-[10px] uppercase tracking-[0.3em] text-cream/50">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-lime" />
        </motion.span>
      </motion.div>
    </section>
  );
}
